'use client';

import { useNavigate } from 'react-router-dom';
import { usePerspectiveData } from '@/hooks/usePerspectiveData';
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { perspectiveFaces, type PerspectiveFace, type ExperienceTile } from '@/data/perspectiveData';
import { resolveIcon } from '@/lib/iconMap';
import { X, Circle } from 'lucide-react';
import TileDetailModal from './TileDetailModal';
import { useFocusTrap } from '@/hooks/use-focus-trap';
import { useLanguage } from '@/context/LanguageContext';

// Type-safe image URL extraction
const getImageSrc = (imageUrl: string | { src: string } | undefined): string => {
    if (!imageUrl) return '';
    return typeof imageUrl === 'string' ? imageUrl : imageUrl.src;
};

// Calculate tile sizing pattern - dynamic based on content length
// Extracted to standalone function for performance
const calculateTileLayout = (tile: ExperienceTile, faceId: string) => {
    // Skip family face (private/images) - always single
    // In 20px grid, a "single" tile (approx 80px) would be span 4
    if (faceId === 'family') {
        return { span: 4 };
    }

    // Manual override from data
    if (tile.span) {
        return { span: tile.span };
    }

    // Flexible sizing based on character count to avoid cropping
    // Base row height is 20px. Gap is 8px.
    // Formula: height = span * 20 + (span - 1) * 8 = span * 28 - 8
    // Inverse: span = (height + 8) / 28

    // 1. Estimate Title Height
    // Title is bold, larger font. Assume ~32 chars per line (tuned from 28).
    // Line height approx 18px.
    const titleLines = Math.ceil(tile.title.length / 32) || 1;
    const titleHeight = titleLines * 18;

    // 2. Subtitle & Padding
    // Padding: 24px (12px top + 12px bottom)
    // Subtitle: ~16px
    // Gap between elements: ~8px
    const baseHeight = 24 + titleHeight + 16 + 8;

    // 3. Content Height
    // Font is 11px. Width is approx 200px.
    // Conservative estimate: 40 chars per line (tuned from 34).
    // Line height: 14px.
    const charsPerLine = 40;
    const contentLines = Math.ceil(tile.summary.length / charsPerLine);
    const contentHeight = contentLines * 14;

    // 4. Total Required Height
    const totalHeight = baseHeight + contentHeight;

    // 5. Calculate Span
    let span = Math.ceil((totalHeight + 8) / 28);

    // 6. Clamp Span
    // Min height: ~80px (Span 3 = 76px, Span 4 = 104px) -> Let's go with min Span 3
    if (span < 3) span = 3;
    // Max height: ~280px (Span 10 = 272px)
    if (span > 10) span = 10;

    return { span };
};

interface ImageLightboxProps {
    tile: ExperienceTile;
    onClose: () => void;
}

const ImageLightbox = ({ tile, onClose }: ImageLightboxProps) => {
    const lightboxRef = useRef<HTMLDivElement>(null);
    useFocusTrap(lightboxRef, true, onClose);

    return (
        <div
            ref={lightboxRef}
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={tile.title || "Image view"}
        >
            <button
                onClick={onClose}
                className="absolute top-4 right-4 p-3 rounded-full hover:bg-white/10 transition-colors z-[10001]"
                aria-label="Close lightbox"
            >
                <X size={28} className="text-white" strokeWidth={2.5} />
            </button>
            <div className="flex flex-col items-center gap-4 max-w-[90vw]" style={{ pointerEvents: 'none' }}>
                <img
                    src={getImageSrc(tile.imageUrl)}
                    alt={tile.title}
                    className="max-w-[90vw] max-h-[80vh] object-contain animate-scale-in"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                />
                <div className="text-center px-4 py-3 bg-black/60 backdrop-blur-md rounded-lg max-w-2xl" style={{ pointerEvents: 'auto' }}>
                    <p className="text-lg text-white">{tile.summary}</p>
                </div>
            </div>
        </div>
    );
};

interface CubeFaceProps {
    face: PerspectiveFace;
    position: [number, number, number];
    rotation: [number, number, number];
    onTileClick: (tile: ExperienceTile) => void;
    isMobile: boolean;
    scale: number;
}

// Memoize CubeFace to prevent re-renders when parent (PerspectiveCube3D) re-renders
const CubeFace = React.memo(({ face, position, rotation, onTileClick, isMobile, scale }: CubeFaceProps) => {
    // Keep geometry at base size
    const baseFaceSize = 4.95;

    // SIMPLIFIED: No mobile-specific adjustments needed with single-shape approach
    const containerSize = '495px';
    const distanceFactor = 4; // Consistent across all devices
    const htmlPosition: [number, number, number] = [0, 0, 0.01];

    // Pre-calculate tile layouts to avoid recalculation during render
    // This is especially important as face properties are stable but isMobile changes could trigger re-renders
    const visibleTiles = useMemo(() => {
        return face.tiles.slice(0, 9).map((tile) => ({
            tile,
            layout: calculateTileLayout(tile, face.id)
        }));
    }, [face.tiles, face.id]);

    return (
        <group position={position} rotation={rotation}>
            {/* COMMENTED OUT: Face background geometry - using CSS borders instead */}
            {/* <mesh>
                <planeGeometry args={[baseFaceSize, baseFaceSize]} />
                <meshStandardMaterial
                    color={face.color}
                    transparent
                    opacity={0.85}
                    metalness={0.1}
                    roughness={0.8}
                />
            </mesh> */}

            {/* Face content with HTML - now with CSS borders to create cube edges */}
            <Html
                transform
                center
                distanceFactor={distanceFactor}
                position={htmlPosition}
                style={{
                    width: containerSize,
                    height: containerSize,
                    pointerEvents: 'none',
                    userSelect: 'none',
                    // CSS borders to create cube edges
                    border: `2px solid ${face.color}`,
                    boxShadow: `0 0 20px ${face.color}80, inset 0 0 40px ${face.color}20`,
                    backgroundColor: `${face.color}15`
                }}
            >
                <div
                    suppressHydrationWarning
                    className="w-full h-full flex flex-col duration-300 p-4"
                    style={{ pointerEvents: 'auto' }}
                >
                    {/* Face subtitle only - title is on the dial */}
                    <div
                        className="text-center mb-3 rounded-lg p-3 backdrop-blur-md"
                        style={{
                            backgroundColor: `${face.color}E6`,
                            border: `1.5px solid ${face.color}`,
                            boxShadow: `0 0 12px ${face.color}80, 0 0 24px ${face.color}40, inset 0 0 8px ${face.color}30, 0 3px 6px rgba(0,0,0,0.3)`
                        }}
                    >
                        <p className="text-sm text-white opacity-90 leading-relaxed">
                            {face.subtitle}
                        </p>
                    </div>

                    {/* Masonry-style tile grid - Windows 8 style */}
                    <div
                        className="flex-1"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gridAutoRows: '20px',
                            gridAutoFlow: 'dense', // Fills gaps automatically
                            gap: '8px',
                            pointerEvents: 'auto'
                        }}
                    >
                        {visibleTiles.map(({ tile, layout }) => {
                            const IconComponent = resolveIcon(tile.icon);

                            return (
                                <button
                                    key={tile.id}
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent click from passing through
                                        onTileClick(tile);
                                    }}
                                    className="relative rounded-md p-3 flex flex-col w-full text-left backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] active:scale-95 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent outline-none group"
                                    style={{
                                        backgroundColor: `${face.color}E6`,
                                        border: `1.5px solid ${face.color}`,
                                        boxShadow: `0 0 12px ${face.color}80, 0 0 24px ${face.color}40, inset 0 0 8px ${face.color}30, 0 3px 6px rgba(0,0,0,0.3)`,
                                        gridRow: `span ${layout.span}`,
                                        overflow: 'hidden',
                                        pointerEvents: 'auto',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {tile.type === 'image' && tile.imageUrl ? (
                                        <div className="w-full h-full relative">
                                            <img
                                                src={getImageSrc(tile.imageUrl)}
                                                alt={tile.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                                                <div className="text-sm text-white">{tile.summary}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col h-full">
                                            {/* Header with icon and title */}
                                            <div className="flex items-start gap-2 mb-1.5">
                                                <IconComponent size={22} className="text-white flex-shrink-0 mt-0.5 drop-shadow-[0_0_6px_rgba(255,255,255,0.5)]" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-bold leading-tight text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.3)] line-clamp-2">
                                                        {tile.title}
                                                    </div>
                                                    <div className="text-[11px] opacity-90 text-white mt-0.5 font-medium">
                                                        {tile.period}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Description - shown on all tiles, full text expansion */}
                                            <div className="text-[11px] leading-snug text-white opacity-95">
                                                {tile.summary}
                                            </div>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </Html>
        </group>
    );
});

// Display name for debugging
CubeFace.displayName = 'CubeFace';

interface PerspectiveCube3DProps {
    faces: PerspectiveFace[];
    targetRotation: { x: number; y: number };
    mouseRef: React.MutableRefObject<{ x: number; y: number } | null>;
    parallaxOrigin: { x: number; y: number };
    onTileClick: (tile: ExperienceTile) => void;
}

const PerspectiveCube3D: React.FC<PerspectiveCube3DProps> = ({ faces, targetRotation, mouseRef, parallaxOrigin, onTileClick }) => {
    const groupRef = useRef<THREE.Group>(null);
    const { language } = useLanguage();
    const [isMobile, setIsMobile] = useState(false);

    // Track window width for mobile detection
    useEffect(() => {
        const updateMobile = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
        };

        updateMobile();
        window.addEventListener('resize', updateMobile);
        return () => window.removeEventListener('resize', updateMobile);
    }, []);

    // Smooth mouse parallax effect and rotation interpolation
    // NOTE: This lerp (0.08 factor) handles BOTH the parallax tilt AND the main rotation transition.
    // We removed the conflicting GSAP animation because this loop already smoothly interpolates to targetRotation.
    useFrame(() => {
        if (groupRef.current) {
            let targetTiltX = 0;
            let targetTiltY = 0;

            // Only apply tilt if we have a valid mouse position
            if (mouseRef.current) {
                // Calculate relative mouse position from the parallax origin (dial bottom arrow)
                const relativeX = mouseRef.current.x - parallaxOrigin.x;
                const relativeY = mouseRef.current.y - parallaxOrigin.y;

                // Add subtle tilt based on relative mouse position (max 0.15 radians = ~8.5 degrees) - reduced by 50%
                targetTiltX = relativeY * 0.15;
                targetTiltY = relativeX * 0.15;
            }

            // Smoothly interpolate current rotation towards target (including tilt)
            groupRef.current.rotation.x += (targetRotation.x + targetTiltX - groupRef.current.rotation.x) * 0.08;
            groupRef.current.rotation.y += (targetRotation.y + targetTiltY - groupRef.current.rotation.y) * 0.08;
        }
    });

    // Memoize faceConfigs to stabilize props for CubeFace
    const faceConfigs: CubeFaceProps[] = useMemo(() => {
        return faces.map((face) => {
            const config = getFaceConfig(face.id);
            return {
                face,
                position: config.position,
                rotation: config.rotation,
                scale: config.scale,
                onTileClick,
                isMobile,
            };
        });
    }, [faces, onTileClick, isMobile]);

    // Keep core at base size
    const baseCoreSize = 4.95;

    // Adjust group position to maintain visual centering
    // On mobile, shift slightly left to center better
    const groupYPosition = -0.6;
    const groupXPosition = isMobile ? -0.15 : 0;

    return (
        <group ref={groupRef} position={[groupXPosition, groupYPosition, 0]} scale={isMobile ? [0.74, 0.74, 0.74] : [1, 1, 1]}>
            {/* COMMENTED OUT: Inner core for depth - using CSS styling instead */}
            {/* <mesh>
                <boxGeometry args={[baseCoreSize, baseCoreSize, baseCoreSize]} />
                <meshPhysicalMaterial
                    color="#050505"
                    roughness={0.8}
                    transmission={0.2}
                    thickness={2}
                    transparent
                    side={THREE.DoubleSide}
                    depthWrite={false}
                />
            </mesh> */}

            {/* COMMENTED OUT: Cube edges - using CSS borders instead */}
            {/* <lineSegments>
                <edgesGeometry args={[new THREE.BoxGeometry(baseCoreSize, baseCoreSize, baseCoreSize)]} />
                <lineBasicMaterial color="#ffffff" opacity={0.85} transparent linewidth={2} />
            </lineSegments> */}

            {/* Render all faces */}
            {faceConfigs.map((config) => (
                <CubeFace
                    key={`${config.face.id}-${language}`}
                    face={config.face}
                    position={config.position}
                    rotation={config.rotation}
                    onTileClick={config.onTileClick}
                    isMobile={config.isMobile}
                    scale={config.scale}
                />
            ))}
        </group>
    );
};

interface PerspectiveCubeProps {
    activeFace: string;
    onFaceChange: (faceId: string) => void;
    parallaxOrigin?: { x: number; y: number };
}

const faceRotations: Record<string, { x: number; y: number }> = {
    products: { x: 0, y: 0 },                    // Front - facing camera
    people: { x: 0, y: -Math.PI / 2 },          // Right - rotate left to face camera
    organizations: { x: 0, y: -Math.PI },        // Back - rotate 180 to face camera
    competences: { x: 0, y: Math.PI / 2 },      // Left - rotate right to face camera
    family: { x: Math.PI / 2, y: 0 },           // Top - tilt down to face camera
    community: { x: -Math.PI / 2, y: 0 }        // Bottom - tilt up to face camera
};

// Position offset is half of 4.95 = 2.475
const baseOffset = 2.475;

const getFaceConfig = (faceId: string): { position: [number, number, number]; rotation: [number, number, number]; scale: number } => {
    switch (faceId) {
        case 'products': // Front
            return { position: [0, 0, baseOffset], rotation: [0, 0, 0], scale: 1 };
        case 'people': // Right
            return { position: [baseOffset, 0, 0], rotation: [0, Math.PI / 2, 0], scale: 1 };
        case 'organizations': // Back
            return { position: [0, 0, -baseOffset], rotation: [0, Math.PI, 0], scale: 1 };
        case 'competences': // Left
            return { position: [-baseOffset, 0, 0], rotation: [0, -Math.PI / 2, 0], scale: 1 };
        case 'family': // Top
            return { position: [0, baseOffset, 0], rotation: [-Math.PI / 2, 0, 0], scale: 1 };
        case 'community': // Bottom
            return { position: [0, -baseOffset, 0], rotation: [Math.PI / 2, 0, 0], scale: 1 };
        default:
            return { position: [0, 0, 0], rotation: [0, 0, 0], scale: 1 };
    }
};

// Cache mobile user agent check to avoid running regex on every mouse move
// This optimization significantly reduces overhead in the mousemove handler
const isMobileUserAgent = typeof navigator !== 'undefined'
    ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    : false;

const PerspectiveCube: React.FC<PerspectiveCubeProps> = ({ activeFace, onFaceChange, parallaxOrigin = { x: 0, y: 0 } }) => {
    // 1. Hooks & State (Must be first)
    const { language } = useLanguage();
    const faces = usePerspectiveData();
    const [targetRotation, setTargetRotation] = useState({ x: 0, y: 0 });
    const mouseRef = useRef<{ x: number; y: number } | null>(null);
    const [selectedTile, setSelectedTile] = useState<ExperienceTile | null>(null);
    const [selectedImageTile, setSelectedImageTile] = useState<ExperienceTile | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 2. Handlers (Must be defined before use in faceConfigs)
    // Wrap handleTileClick in useCallback to prevent recreating it on every render
    const handleTileClick = useCallback((tile: ExperienceTile) => {
        // Handle image tiles - open lightbox
        if (tile.type === 'image' && tile.imageUrl) {
            setSelectedImageTile(tile);
            return;
        }

        // Only open modal for tiles with detailed view
        if (tile.detailedView) {
            setSelectedTile(tile);
            setIsModalOpen(true);
        }
    }, []); // Empty dependencies as state setters are stable

    // 3. Derived State / Memoization (Depends on faces & handleTileClick)
    // No longer needed here as logic moved to PerspectiveCube3D

    // 4. Effects
    // Track mouse position for parallax effect (desktop only)
    useEffect(() => {
        // Cache window dimensions to avoid layout thrashing
        let windowWidth = window.innerWidth;
        let windowHeight = window.innerHeight;

        const handleResize = () => {
            windowWidth = window.innerWidth;
            windowHeight = window.innerHeight;
        };

        window.addEventListener('resize', handleResize);

        // Optimized check using cached regex result
        const isMobileDevice = () => {
            return isMobileUserAgent || windowWidth < 768;
        };

        const handleMouseMove = (e: MouseEvent) => {
            // Skip mouse tracking on mobile devices
            if (isMobileDevice()) {
                return;
            }

            // Normalize to -1 to 1 range
            const x = (e.clientX / windowWidth) * 2 - 1;
            const y = -(e.clientY / windowHeight) * 2 + 1;
            mouseRef.current = { x, y };
        };

        // Only add listener on desktop
        if (!isMobileDevice()) {
            window.addEventListener('mousemove', handleMouseMove);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedTile(null), 300); // Clear after animation
    };

    // Update rotation when activeFace changes
    useEffect(() => {
        setTargetRotation(faceRotations[activeFace]);
    }, [activeFace]);

    return (
        <>
            <div className="relative w-full h-full" style={{ zIndex: 1 }}>
                <Canvas
                    camera={{ position: [0, 0, 8.5], fov: 60 }}
                    style={{ background: 'transparent', touchAction: 'pan-y', position: 'relative', zIndex: 1 }}
                    gl={{ antialias: true, alpha: true }}
                    onWheel={(e) => e.stopPropagation()}
                >
                    <ambientLight intensity={0.7} />
                    {/* Enhanced dramatic colored lighting for better depth */}
                    <pointLight position={[10, 10, 10]} intensity={2.2} color="#06B6D4" />
                    <pointLight position={[-10, -10, -10]} intensity={1.7} color="#8B5CF6" />
                    <pointLight position={[0, 10, -10]} intensity={1.4} color="#EC4899" />

                    <PerspectiveCube3D
                        faces={faces}
                        targetRotation={targetRotation}
                        mouseRef={mouseRef}
                        parallaxOrigin={parallaxOrigin}
                        onTileClick={handleTileClick}
                    />
                </Canvas>
            </div>

            {/* Render modals via portal to escape z-index stacking context */}
            {typeof document !== 'undefined' && ReactDOM.createPortal(
                <>
                    {/* Tile Detail Modal */}
                    {selectedTile && (
                        <TileDetailModal
                            tile={selectedTile}
                            faceColor={perspectiveFaces.find(f => f.tiles.some(t => t.id === selectedTile.id))?.color || '#ffffff'}
                            isOpen={isModalOpen}
                            onClose={handleCloseModal}
                        />
                    )}

                    {/* Image Lightbox */}
                    {selectedImageTile && (
                        <ImageLightbox
                            tile={selectedImageTile}
                            onClose={() => setSelectedImageTile(null)}
                        />
                    )}
                </>,
                document.body
            )}
        </>
    );
};

export default PerspectiveCube;
